// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';

import { type PlacementRenderProps } from '@post-office/browser--rovo-expansion/rovo-chat-input-full-page';

import { NudgeBanner } from '../nudge-banner';

import { type ConnectorInfo, type ConnectorNudgeProps } from '../../types';

import { type MessageTemplateContext } from '../../../index';

import { type JSX } from 'react/jsx-runtime';

export type MessageFrontendProps = MessageTemplateContext &
	PlacementRenderProps & {
		connectorNudgeMessageProps?: ConnectorNudgeProps;
	};

/**
 * Selects the next connector to show using round-robin rotation.
 *
 * Filters out dismissed connectors (from the TAP dismissals trait), then picks
 * the connector after the last-shown one (from the TAP last-shown trait). If the
 * last-shown connector is not in the eligible list, starts from the beginning.
 * Returns null if no eligible connectors remain.
 */
export function selectConnectorForBanner(
	apps: ConnectorInfo[],
	dismissedConnectorIds: string,
	lastShownConnectorId: string,
): ConnectorInfo | null {
	const dismissed = dismissedConnectorIds
		? dismissedConnectorIds.split(',').map((id) => id.trim())
		: [];
	const eligible = apps.filter((app) => !dismissed.includes(app.connectorId));

	if (eligible.length === 0) {
		return null;
	}

	if (eligible.length === 1 || !lastShownConnectorId) {
		return eligible[0] ?? null;
	}

	const lastIndex = eligible.findIndex((app) => app.connectorId === lastShownConnectorId);
	// If lastShownConnectorId isn't in the eligible list, start at 0; otherwise advance by 1
	const nextIndex = lastIndex === -1 ? 0 : (lastIndex + 1) % eligible.length;
	return eligible[nextIndex] ?? null;
}

export const MessageFrontendComponent = ({
	dismiss,
	connectorNudgeMessageProps,
	dismissedConnectorIds,
	lastShownConnectorId,
}: MessageFrontendProps): JSX.Element | null => {
	const { apps } = connectorNudgeMessageProps ?? {};

	const connector = useRef(
		apps ? selectConnectorForBanner(apps, dismissedConnectorIds, lastShownConnectorId) : null,
	);
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();

	useEffect(() => {
		if (connector.current) {
			void messageInstanceViewed({
				connectorId: connector.current.connectorId,
			});
		}
	}, []);

	const handleDismiss = useCallback(() => {
		if (!connector.current) {
			return;
		}

		void onClosed({
			interactionIdentifier: 'rovo-chat-3p-connector-nudge-dismiss',
			additionalMetadata: {
				connectorId: connector.current.connectorId,
				appName: connector.current.appName,
			},
		});
		dismiss();
	}, [onClosed, dismiss]);

	const handleConnect = useCallback(() => {
		if (!connector.current) {
			return;
		}

		void onCtaActioned({
			interactionIdentifier: 'rovo-chat-3p-connector-nudge-connect',
			additionalMetadata: {
				connectorId: connector.current.connectorId,
				appName: connector.current.appName,
			},
		});
		connector.current.onConnect();
		dismiss();
	}, [onCtaActioned, dismiss]);

	if (!connector.current) {
		return null;
	}

	return (
		<ManualMessageViewedEvent>
			<NudgeBanner
				appName={connector.current.appName}
				logoUrl={connector.current.logoUrl}
				onConnect={handleConnect}
				onDismiss={handleDismiss}
			/>
		</ManualMessageViewedEvent>
	);
};
